import { Constructor } from '@angular/cdk/table';
import { FormGroup } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { compareArrays } from '../utils/array-utils';
import { Subject } from 'rxjs';

type WithContentVisibilityFormSectionType = {
  _initContentVisibility(
    restrictToRegisteredUsers: boolean,
    notForGoogleNewsSitemaps: boolean,
    notForNewsFeeds: boolean,
    hideThisArticle: boolean,
    paywallPremiumContent: boolean,
    notForCompetitors: boolean
  ): void;
  _initContentVisibilityValueChanges(
    getRestrictToRegisteredUsersValue: () => boolean | null,
    getNotForGoogleNewsSitemapsValue: () => boolean | null,
    getNotForNewsFeedsValue: () => boolean | null,
    getHideThisArticleValue: () => boolean | null,
    getPaywallPremiumContent: () => boolean | null,
    getNotForCompetitors: () => boolean | null
  ): void;
  _buildRestrictToRegisteredUsers(): boolean;
  _buildNotForGoogleNewsSitemaps(): boolean;
  _buildNotForNewsFeeds(): boolean;
  _buildHideThisArticle(): boolean;
  _buildPaywallPremiumContent(): boolean;
  _buildNotForCompetitors(): boolean;
  get restrictToRegisteredUsers(): boolean;
  get notForGoogleNewsSitemaps(): boolean;
  get notForNewsFeeds(): boolean;
  get hideThisArticle(): boolean;
  get paywallPremiumContent(): boolean;
  get notForCompetitors(): boolean;
};

type ContentVisibilityFormSectionRequerimentsBase = {
  form: FormGroup;
  FORM_PATHS: {
    RESTRICTED_TO_REGISTERED_USERS: () => string;
    NOT_FOR_GOOGLE_NEWS_SITEMAPS: () => string;
    NOT_FOR_NEWS_FEED: () => string;
    HIDE_THIS_ARTICLE: () => string;
    PAYWALL_PREMIUM_CONTENT: () => string;
    NOT_FOR_COMPETITORS: () => string;
  };
  destroy$: Subject<boolean>;
};

export function WithContentVisibilityFormSection<
  TBase extends Constructor<ContentVisibilityFormSectionRequerimentsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithContentVisibilityFormSectionType;
  prototype: WithContentVisibilityFormSectionType;
} & TBase {
  return class WithContentVisibilityFormSectionable extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    _initContentVisibility(
      restrictToRegisteredUsers: boolean,
      notForGoogleNewsSitemaps: boolean,
      notForNewsFeeds: boolean,
      hideThisArticle: boolean,
      paywallPremiumContent: boolean,
      notForCompetitors: boolean
    ): void {
      this.form
        .get(this.FORM_PATHS.RESTRICTED_TO_REGISTERED_USERS())
        .setValue(restrictToRegisteredUsers);
      this.form
        .get(this.FORM_PATHS.NOT_FOR_GOOGLE_NEWS_SITEMAPS())
        .setValue(notForGoogleNewsSitemaps);
      this.form
        .get(this.FORM_PATHS.NOT_FOR_NEWS_FEED())
        .setValue(notForNewsFeeds);
      this.form
        .get(this.FORM_PATHS.HIDE_THIS_ARTICLE())
        .setValue(hideThisArticle);
      this.form
        .get(this.FORM_PATHS.PAYWALL_PREMIUM_CONTENT())
        .setValue(paywallPremiumContent);
      this.form
        .get(this.FORM_PATHS.NOT_FOR_COMPETITORS())
        .setValue(notForCompetitors);
    }

    _initContentVisibilityValueChanges(
      getPreviousRestrictToRegisteredUsersValue: () => boolean | null,
      getPreviousNotForGoogleNewsSitemapsValue: () => boolean | null,
      getPreviousNotForNewsFeedsValue: () => boolean | null,
      getPreviousHideThisArticleValue: () => boolean | null,
      getPreviousPaywallPremiumContent: () => boolean | null,
      getPreviousNotForCompetitors: () => boolean | null
    ): void {
      this.form
        .get(this.FORM_PATHS.RESTRICTED_TO_REGISTERED_USERS())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue === getPreviousRestrictToRegisteredUsersValue()) {
              this.form
                .get(this.FORM_PATHS.RESTRICTED_TO_REGISTERED_USERS())
                .markAsPristine();
            } else {
              this.form
                .get(this.FORM_PATHS.RESTRICTED_TO_REGISTERED_USERS())
                .markAsDirty();
            }
            if (!newValue) {
              this.form
                .get(this.FORM_PATHS.PAYWALL_PREMIUM_CONTENT())
                .setValue(false);
              this.form
                .get(this.FORM_PATHS.NOT_FOR_COMPETITORS())
                .setValue(false);
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.NOT_FOR_GOOGLE_NEWS_SITEMAPS())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue === getPreviousNotForGoogleNewsSitemapsValue()) {
              this.form
                .get(this.FORM_PATHS.NOT_FOR_GOOGLE_NEWS_SITEMAPS())
                .markAsPristine();
            } else {
              this.form
                .get(this.FORM_PATHS.NOT_FOR_GOOGLE_NEWS_SITEMAPS())
                .markAsDirty();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.NOT_FOR_NEWS_FEED())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue === getPreviousNotForNewsFeedsValue()) {
              this.form
                .get(this.FORM_PATHS.NOT_FOR_NEWS_FEED())
                .markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.NOT_FOR_NEWS_FEED()).markAsDirty();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.HIDE_THIS_ARTICLE())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue === getPreviousHideThisArticleValue()) {
              this.form
                .get(this.FORM_PATHS.HIDE_THIS_ARTICLE())
                .markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.HIDE_THIS_ARTICLE()).markAsDirty();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.PAYWALL_PREMIUM_CONTENT())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue === getPreviousPaywallPremiumContent()) {
              this.form
                .get(this.FORM_PATHS.PAYWALL_PREMIUM_CONTENT())
                .markAsPristine();
            } else {
              this.form
                .get(this.FORM_PATHS.PAYWALL_PREMIUM_CONTENT())
                .markAsDirty();
            }
            if (newValue) {
              this.form
                .get(this.FORM_PATHS.RESTRICTED_TO_REGISTERED_USERS())
                .setValue(true);
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.NOT_FOR_COMPETITORS())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue === getPreviousNotForCompetitors()) {
              this.form
                .get(this.FORM_PATHS.NOT_FOR_COMPETITORS())
                .markAsPristine();
            } else {
              this.form
                .get(this.FORM_PATHS.NOT_FOR_COMPETITORS())
                .markAsDirty();
            }
            if (newValue) {
              this.form
                .get(this.FORM_PATHS.RESTRICTED_TO_REGISTERED_USERS())
                .setValue(true);
            }
          },
        });
    }

    _buildRestrictToRegisteredUsers(): boolean {
      return this.form.get(this.FORM_PATHS.RESTRICTED_TO_REGISTERED_USERS())
        .value;
    }

    _buildNotForGoogleNewsSitemaps(): boolean {
      return this.form.get(this.FORM_PATHS.NOT_FOR_GOOGLE_NEWS_SITEMAPS())
        .value;
    }

    _buildNotForNewsFeeds(): boolean {
      return this.form.get(this.FORM_PATHS.NOT_FOR_NEWS_FEED()).value;
    }

    _buildHideThisArticle(): boolean {
      return this.form.get(this.FORM_PATHS.HIDE_THIS_ARTICLE()).value;
    }

    _buildPaywallPremiumContent(): boolean {
      return this.form.get(this.FORM_PATHS.PAYWALL_PREMIUM_CONTENT()).value;
    }

    _buildNotForCompetitors(): boolean {
      return this.form.get(this.FORM_PATHS.NOT_FOR_COMPETITORS()).value;
    }

    get restrictToRegisteredUsers(): boolean {
      return this.form.get(this.FORM_PATHS.RESTRICTED_TO_REGISTERED_USERS())
        .value;
    }

    get notForGoogleNewsSitemaps(): boolean {
      return this.form.get(this.FORM_PATHS.NOT_FOR_GOOGLE_NEWS_SITEMAPS())
        .value;
    }

    get notForNewsFeeds(): boolean {
      return this.form.get(this.FORM_PATHS.NOT_FOR_NEWS_FEED()).value;
    }

    get hideThisArticle(): boolean {
      return this.form.get(this.FORM_PATHS.HIDE_THIS_ARTICLE()).value;
    }

    get paywallPremiumContent(): boolean {
      return this.form.get(this.FORM_PATHS.PAYWALL_PREMIUM_CONTENT()).value;
    }

    get notForCompetitors(): boolean {
      return this.form.get(this.FORM_PATHS.NOT_FOR_COMPETITORS()).value;
    }
  };
}
